import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Crown, Swords, Zap, Trophy, BookOpen } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxDigestPreview from './ArenaxDigestPreview';
import ArenaxCityPulseTicker from './ArenaxCityPulseTicker';

/**
 * Phase 2M PR 1 — Sports Stories page.
 *
 * Editorial magazine surface.  Each card displays a story composed by the
 * narrative-primitives engine.  Tone: passionate local sports desk.
 */
export default function ArenaxStories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('Bengaluru');
  const [sport, setSport] = useState('Football');
  const [kind, setKind] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sport) params.set('sport', sport);
    if (kind && kind !== 'all') params.set('kind', kind);
    params.set('limit', '30');
    api.get(`/arenax/stories?${params.toString()}`)
      .then(r => setStories(r.data?.stories || []))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, [city, sport, kind]);

  const leadStory = useMemo(() => stories.find(s => s.kind === 'season_retrospective' || s.kind === 'match_recap') || stories[0], [stories]);
  const restStories = useMemo(() => stories.filter(s => s !== leadStory), [stories, leadStory]);

  return (
    <ArenaxLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-stories-page">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-500/15 p-6 lg:p-8 mb-6"
          style={{ background: 'var(--ax-card-gradient)' }}>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">
              Stories from the city
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-100 leading-tight mb-3">
            <span className="arenax-hero-gradient-text">
              The city's sports desk
            </span>
          </h1>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
            Rivalry previews, match recaps, season retrospectives — short, punchy, local. Written from the heartbeat of every league in your city.
          </p>
        </div>

        {/* Phase 128 — Live in the City ambient ticker (booking-driven civic motion) */}
        <section className="mb-6" data-testid="arenax-city-pulse-section">
          <ArenaxCityPulseTicker city={city} maxItems={8} />
        </section>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <KindButton label="Everything" active={kind === 'all'} onClick={() => setKind('all')} testid="arenax-stories-kind-all" />
          <KindButton label="Previews" active={kind === 'rivalry_preview'} onClick={() => setKind('rivalry_preview')} testid="arenax-stories-kind-preview" />
          <KindButton label="Recaps" active={kind === 'match_recap'} onClick={() => setKind('match_recap')} testid="arenax-stories-kind-recap" />
          <KindButton label="Retrospectives" active={kind === 'season_retrospective'} onClick={() => setKind('season_retrospective')} testid="arenax-stories-kind-retro" />
        </div>

        {/* Phase 2M PR 3 — Weekly digest preview (renders only when a digest exists) */}
        <ArenaxDigestPreview city={city} sport={sport} />

        {loading ? (
          <div className="space-y-3" data-testid="arenax-stories-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 rounded-2xl border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-12 px-6 rounded-2xl border border-dashed border-slate-700/60"
            data-testid="arenax-stories-empty">
            <BookOpen className="mx-auto h-8 w-8 text-slate-500 mb-3" />
            <h3 className="text-lg font-black text-slate-100 mb-1">No stories yet.</h3>
            <p className="text-sm text-slate-400">Once matches finish + splits crown champions, the stories start writing themselves.</p>
          </div>
        ) : (
          <>
            {leadStory && <LeadStoryCard story={leadStory} />}
            {restStories.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="arenax-stories-grid">
                {restStories.map(s => <StoryCard key={s.id} story={s} />)}
              </div>
            )}
          </>
        )}
      </div>
    </ArenaxLayout>
  );
}

const KIND_META = {
  rivalry_preview: { label: 'PREVIEW', Icon: Flame, accent: 'text-rose-200',
                       border: 'border-rose-500/35',
                       bg: 'linear-gradient(135deg, rgba(244,63,94,0.10), var(--ax-card-bg-soft))' },
  match_recap: { label: 'RECAP', Icon: Swords, accent: 'text-amber-200',
                  border: 'border-amber-500/35',
                  bg: 'linear-gradient(135deg, rgba(245,158,11,0.10), var(--ax-card-bg-soft))' },
  season_retrospective: { label: 'RETROSPECTIVE', Icon: Crown, accent: 'text-amber-100',
                           border: 'border-amber-500/45',
                           bg: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(244,63,94,0.06), var(--ax-card-bg-soft))' },
};

function LeadStoryCard({ story }) {
  const meta = KIND_META[story.kind] || KIND_META.match_recap;
  const { Icon, accent, border, bg, label } = meta;
  const deeplink = story.match_id ? `/arenax/tournaments/${story.tournament_id}`
                    : story.tournament_id ? `/arenax/tournaments/${story.tournament_id}`
                    : story.season_id ? `/arenax/seasons/${story.season_id}` : '/arenax';
  return (
    <Link to={deeplink}
      data-testid={`arenax-story-lead-${story.id}`}
      className={`block rounded-3xl border ${border} p-6 lg:p-8 mb-6 transition-all hover:translate-y-[-2px]`}
      style={{ background: bg }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className={`text-[10px] uppercase tracking-[0.3em] font-black ${accent}`}>
          {label}
        </span>
        <PrimitiveBadges primitives={story.primitives} />
      </div>
      <h2 className="text-2xl sm:text-3xl font-black text-slate-100 leading-tight mb-3"
        data-testid={`arenax-story-headline-${story.id}`}>
        {story.headline}
      </h2>
      <p className="text-base sm:text-lg text-slate-200 leading-relaxed whitespace-pre-line"
        data-testid={`arenax-story-body-${story.id}`}>
        {story.body || story.story.split('\n\n').slice(1).join('\n\n')}
      </p>
    </Link>
  );
}

function StoryCard({ story }) {
  const meta = KIND_META[story.kind] || KIND_META.match_recap;
  const { Icon, accent, border, bg, label } = meta;
  const deeplink = story.match_id ? `/arenax/tournaments/${story.tournament_id}`
                    : story.tournament_id ? `/arenax/tournaments/${story.tournament_id}`
                    : story.season_id ? `/arenax/seasons/${story.season_id}` : '/arenax';
  return (
    <Link to={deeplink}
      data-testid={`arenax-story-card-${story.id}`}
      className={`block rounded-2xl border ${border} p-5 transition-all hover:translate-y-[-1px]`}
      style={{ background: bg }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-3.5 w-3.5 ${accent}`} />
        <span className={`text-[9px] uppercase tracking-[0.25em] font-black ${accent}`}>
          {label}
        </span>
        <PrimitiveBadges primitives={story.primitives} compact />
      </div>
      <h3 className="text-base font-black text-slate-100 leading-snug mb-2">
        {story.headline}
      </h3>
      <p className="text-sm text-slate-300 leading-snug whitespace-pre-line line-clamp-4">
        {story.body || story.story.split('\n\n').slice(1).join('\n\n')}
      </p>
    </Link>
  );
}

const PRIMITIVE_EMOJI = {
  revenge: '🔥', streak: '⚔️', dynasty: '👑', comeback: '⚡',
  upset: '👀', breakthrough: '🥇', momentum: '💪', redemption: '🏆', collapse: '⚔️', underdog: '👀',
};

function PrimitiveBadges({ primitives, compact = false }) {
  if (!primitives || primitives.length === 0) return null;
  const top = primitives.slice(0, 2);
  return (
    <div className="ml-auto flex gap-1">
      {top.map((p, i) => (
        <span key={i}
          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold text-slate-300 bg-slate-900 border border-slate-700`}
          data-testid={`arenax-story-primitive-${p.type}`}>
          {PRIMITIVE_EMOJI[p.type] || '🔥'}
          {!compact && p.type}
        </span>
      ))}
    </div>
  );
}

function KindButton({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
        active ? 'text-amber-200 bg-amber-500/15 border border-amber-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
      }`}>{label}</button>
  );
}
